import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/api/base44Client";
import { setGlobalTimezone } from "@/lib/timeUtils";

const ClientSessionContext = createContext(null);

export function ClientSessionProvider({ children }) {
  const [clientUser, setClientUser] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(true);

  const logoutClient = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("Sign out error:", e);
    }
    setClientUser(null);
    try {
      localStorage.removeItem('satala_client_user');
      localStorage.removeItem('satala_client_remembered_pass');
      sessionStorage.clear();
    } catch (e) {}

    // Redirect to /login if currently on a protected client page
    const clientPages = ['/pesan-po', '/daftar-pesanan', '/pesanan', '/database-produk', '/profil-saya', '/dashboard-client'];
    const currentPath = window.location.pathname;
    if (clientPages.some(p => currentPath.startsWith(p))) {
      window.location.href = '/login';
    }
  };

  const fetchClientProfile = async (session) => {
    if (!session?.user?.email) {
      setClientUser(null);
      setIsLoadingClient(false);
      return;
    }

    try {
      const userEmail = session.user.email.trim().toLowerCase();
      
      // Query ultra-ringan: Hanya ambil 1 baris record user ini saja (hemat kuota & CPU)
      let row = null;
      const { data: targetedRows, error: targetError } = await supabase
        .from('ClientAccount')
        .select('id, data, created_date, updated_date')
        .eq('data->>email', userEmail)
        .limit(1);

      if (!targetError && targetedRows && targetedRows.length > 0) {
        row = targetedRows[0];
      } else {
        // Fallback pencarian case-insensitive jika format JSONB berbeda
        const { data: allRows, error: fallbackError } = await supabase
          .from('ClientAccount')
          .select('id, data, created_date, updated_date');
        
        if (fallbackError) {
          console.warn("Error fetching client profile:", fallbackError.message);
          return;
        }
        row = allRows?.find(r => (r.data?.email || '').trim().toLowerCase() === userEmail);
      }

      if (row) {
        const isDeleted = Boolean(row.data?.deleted_at) || 
                          ['inactive', 'nonaktif', 'deleted', 'blocked', 'suspended'].includes((row.data?.status || '').toLowerCase());

        if (isDeleted) {
          console.warn("[Security] Akun klien telah dihapus atau dinonaktifkan oleh administrator. Memutus sesi aktif...");
          await logoutClient();
          return;
        }

        const clientData = { ...row.data, id: row.id, created_date: row.created_date, updated_date: row.updated_date };
        setClientUser(clientData);
        if (clientData?.timezone) {
          setGlobalTimezone(clientData.timezone);
        }
      } else {
        // Client account not found in database (hard deleted or removed)
        console.warn("[Security] Akun klien tidak lagi ditemukan di database. Memutus sesi aktif...");
        await logoutClient();
      }
    } catch (e) {
      console.warn("Network error fetching client profile:", e);
    } finally {
      setIsLoadingClient(false);
    }
  };

  const refreshClient = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetchClientProfile(session);
    } catch (e) {
      console.warn("Refresh client session error:", e);
    }
  };

  useEffect(() => {
    // Check active session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      fetchClientProfile(session);
    });

    // Listen for auth changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      fetchClientProfile(session);
      
      // Intercept password recovery flow to prevent redirecting to dashboard
      if (_event === 'PASSWORD_RECOVERY') {
        window.location.href = '/profil-saya?tab=keamanan';
      }
    });

    // Cek status saat tab kembali dibuka dengan pengaman Throttle (Zero Overhead)
    let lastChecked = Date.now();
    const handleFocus = () => {
      if (document.hidden) return;
      const now = Date.now();
      // Throttle: Jika sudah dicek dalam 30 detik terakhir, jangan request lagi
      if (now - lastChecked < 30000) return;
      lastChecked = now;
      refreshClient();
    };
    window.addEventListener('visibilitychange', handleFocus);
    window.addEventListener('focus', handleFocus);

    // Heartbeat pasif tiap 30 menit (HANYA berjalan jika tab aktif, santai dan zero overhead)
    const interval = setInterval(() => {
      if (document.hidden) return;
      lastChecked = Date.now();
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          fetchClientProfile(session);
        }
      });
    }, 30 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, []);

  const loginClient = async (clientData) => {
    setClientUser(clientData);
  };

  return (
    <ClientSessionContext.Provider value={{ clientUser, isLoadingClient, loginClient, logoutClient, refreshClient }}>
      {children}
    </ClientSessionContext.Provider>
  );
}

export const useClientSession = () => {
  const ctx = useContext(ClientSessionContext);
  if (!ctx) throw new Error("useClientSession must be used inside ClientSessionProvider");
  return ctx;
};
